import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  FolderPlus, Upload, LayoutGrid, List as ListIcon, Highlighter, Library,
  FileDown, CheckSquare, Square, BookMarked, Clock, Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErr } from "@/lib/api";
import { EMPTY_STATE_IMG, DEFAULT_COVER } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import AnnotationsView from "@/components/AnnotationsView";
import BorrowManager from "@/components/BorrowManager";
import ProfileBorrows from "@/components/ProfileBorrows";
import {
  coverUrl, descendantIds, ProgressBar, buildVisibleFolders, FolderRow, BookCard, BookRow,
} from "@/components/dashboard/DashboardParts";
import {
  UploadDialog, FolderDialog, FolderRenameDialog, EditBookDialog, ListDialog, LendDialog,
} from "@/components/dashboard/DashboardDialogs";

export default function Dashboard({ openBook }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [folders, setFolders] = useState([]);
  const [books, setBooks] = useState([]);
  const [recent, setRecent] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [view, setView] = useState("tile");
  const [selected, setSelected] = useState(new Set());

  const [uploadOpen, setUploadOpen] = useState(false);
  const [folderOpen, setFolderOpen] = useState(false);
  const [renameFolder, setRenameFolder] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [annOpen, setAnnOpen] = useState(false);
  const [borrowOpen, setBorrowOpen] = useState(false);
  const [editBook, setEditBook] = useState(null);
  const [lendBook, setLendBook] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reload = useCallback(async () => {
    const [f, b, r] = await Promise.all([
      isAdmin ? api.get("/folders") : Promise.resolve({ data: [] }),
      api.get("/books"),
      api.get("/recent"),
    ]);
    setFolders(f.data);
    setBooks(b.data);
    setRecent(r.data);
  }, [isAdmin]);

  useEffect(() => { reload(); }, [reload]);

  const rootFolders = folders.filter((f) => !f.parent_id);
  const childrenOf = (id) => folders.filter((f) => f.parent_id === id);
  const visibleFolderRows = buildVisibleFolders(folders, expanded);
  const deleteFolder = async (id) => { await api.delete(`/folders/${id}`); reload(); };
  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const visibleBooks = useMemo(() => {
    if (!selectedFolder) return books;
    const ids = descendantIds(selectedFolder, folders);
    return books.filter((b) => ids.includes(b.folder_id));
  }, [books, folders, selectedFolder]);

  const toggleSel = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const allSelected = visibleBooks.length > 0 && visibleBooks.every((b) => selected.has(b.id));
  const selectAll = () => setSelected(allSelected ? new Set() : new Set(visibleBooks.map((b) => b.id)));

  const openTheBook = async (book) => {
    await api.post(`/books/${book.id}/open`).catch(() => {});
    openBook(book, book.last_page || 1);
    reload();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/books/${deleteTarget.id}`);
      toast.success("Book deleted");
      setDeleteTarget(null);
      reload();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  const downloadList = async (body, filename) => {
    try {
      const res = await api.post("/lists/recommended", body, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("List downloaded");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  const headerTitle = selectedFolder
    ? folders.find((f) => f.id === selectedFolder)?.name
    : isAdmin ? "My Library" : "Borrowed Books";

  return (
    <div className="h-full flex bg-slate-50 dark:bg-slate-950">
      {isAdmin && (
        <aside className="w-64 shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 overflow-y-auto thin-scrollbar">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Folders</h3>
            <Button data-testid="new-folder-btn" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setFolderOpen(true)}>
              <FolderPlus className="h-4 w-4" />
            </Button>
          </div>
          <button data-testid="folder-all" onClick={() => setSelectedFolder(null)}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${!selectedFolder ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300" : "hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
            <Library className="h-4 w-4" /> All Documents
          </button>
          <div className="mt-1 space-y-0.5">
            {visibleFolderRows.map(({ folder, depth, hasChildren }) => (
              <FolderRow key={folder.id} folder={folder} depth={depth} hasChildren={hasChildren}
                isOpen={!!expanded[folder.id]} onToggleExpand={() => toggleExpand(folder.id)}
                selected={selectedFolder} onSelect={setSelectedFolder} onDelete={deleteFolder} onRename={setRenameFolder} />
            ))}
            {rootFolders.length === 0 && <p className="text-xs text-slate-400 px-2 py-2">No folders yet. Create one to organize your books.</p>}
          </div>
        </aside>
      )}

      <main className="flex-1 min-w-0 overflow-y-auto thin-scrollbar p-6 lg:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-head font-extrabold tracking-tight text-slate-900 dark:text-slate-50">{headerTitle}</h1>
            <p className="text-slate-500 text-sm mt-1">{visibleBooks.length} documents</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="annotations-btn" variant="outline" size="sm" onClick={() => setAnnOpen(true)}>
              <Highlighter className="h-4 w-4 mr-1.5 text-amber-500" /> Annotations
            </Button>
            <Button data-testid={isAdmin ? "borrow-btn" : "myborrows-btn"} variant="outline" size="sm" onClick={() => setBorrowOpen(true)}>
              {isAdmin ? <Users className="h-4 w-4 mr-1.5 text-indigo-600" /> : <BookMarked className="h-4 w-4 mr-1.5 text-indigo-600" />}
              {isAdmin ? "Lending" : "My Borrows"}
            </Button>
            <div className="flex rounded-md border border-slate-200 dark:border-slate-800 overflow-hidden">
              <button data-testid="view-tile" onClick={() => setView("tile")} className={`px-2.5 py-1.5 ${view === "tile" ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button data-testid="view-list" onClick={() => setView("list")} className={`px-2.5 py-1.5 ${view === "list" ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                <ListIcon className="h-4 w-4" />
              </button>
            </div>
            {isAdmin && (
              <Button data-testid="upload-btn" size="sm" onClick={() => setUploadOpen(true)}>
                <Upload className="h-4 w-4 mr-1.5" /> Upload
              </Button>
            )}
          </div>
        </div>

        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2 mb-5 p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <Button data-testid="select-all-btn" variant="ghost" size="sm" onClick={selectAll}>
              {allSelected ? <CheckSquare className="h-4 w-4 mr-1.5" /> : <Square className="h-4 w-4 mr-1.5" />}
              {allSelected ? "Deselect all" : "Select all"}
            </Button>
            <span className="text-sm text-slate-400">{selected.size} selected</span>
            <div className="flex-1" />
            <Button data-testid="create-list-btn" variant="outline" size="sm" onClick={() => setListOpen(true)}>
              <FileDown className="h-4 w-4 mr-1.5" /> Create reading list (PDF)
            </Button>
          </div>
        )}

        {recent.length > 0 && (
          <section className="mb-8">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-500 mb-3"><Clock className="h-4 w-4" /> Recently opened</h2>
            <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
              {recent.map((b) => (
                <button key={b.id} data-testid="recent-book" onClick={() => openTheBook(b)} className="shrink-0 w-28 text-left group">
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800 group-hover:-translate-y-1 transition-transform">
                    <img src={coverUrl(b)} alt={b.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = DEFAULT_COVER; }} />
                    <ProgressBar percent={b.progress} />
                  </div>
                  <p className="mt-1 text-xs font-medium truncate text-slate-700 dark:text-slate-300">{b.title}</p>
                </button>
              ))}
            </div>
          </section>
        )}

        <h2 className="text-sm font-semibold text-slate-500 mb-3">Documents</h2>
        {visibleBooks.length === 0 ? (
          <div className="text-center py-16">
            <img src={EMPTY_STATE_IMG} alt="empty" className="w-64 mx-auto rounded-xl opacity-50 grayscale" />
            <p className="mt-4 text-slate-500">No documents here yet.</p>
            {isAdmin && <Button className="mt-3" onClick={() => setUploadOpen(true)}><Upload className="h-4 w-4 mr-1.5" />Upload your first document</Button>}
          </div>
        ) : view === "tile" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {visibleBooks.map((b) => (
              <BookCard key={b.id} book={b} isAdmin={isAdmin} selected={selected.has(b.id)}
                onToggle={() => toggleSel(b.id)} onOpen={() => openTheBook(b)} onEdit={() => setEditBook(b)} onLend={() => setLendBook(b)} onDelete={() => setDeleteTarget(b)} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {visibleBooks.map((b) => (
              <BookRow key={b.id} book={b} folders={folders} isAdmin={isAdmin} selected={selected.has(b.id)}
                onToggle={() => toggleSel(b.id)} onOpen={() => openTheBook(b)} onEdit={() => setEditBook(b)} onLend={() => setLendBook(b)} onDelete={() => setDeleteTarget(b)} />
            ))}
          </div>
        )}
      </main>

      {uploadOpen && <UploadDialog folders={folders} onClose={() => setUploadOpen(false)} onDone={reload} />}
      {folderOpen && <FolderDialog folders={folders} onClose={() => setFolderOpen(false)} onDone={reload} />}
      {renameFolder && <FolderRenameDialog folder={renameFolder} onClose={() => setRenameFolder(null)} onDone={reload} />}
      {listOpen && <ListDialog selected={selected} selectedFolder={selectedFolder} folders={folders} onClose={() => setListOpen(false)} onDownload={downloadList} />}
      {editBook && <EditBookDialog book={editBook} folders={folders} onClose={() => setEditBook(null)} onDone={reload} />}
      {lendBook && <LendDialog book={lendBook} onClose={() => setLendBook(null)} />}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this book?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.title}" and all its annotations will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={annOpen} onOpenChange={setAnnOpen}>
        <DialogContent className="max-w-[96vw] w-[96vw] h-[88vh] flex flex-col">
          <DialogHeader><DialogTitle className="font-head">Annotations</DialogTitle></DialogHeader>
          <AnnotationsView books={books} onOpenBook={(b, page) => { setAnnOpen(false); openBook(b, page); }} />
        </DialogContent>
      </Dialog>
      <Dialog open={borrowOpen} onOpenChange={setBorrowOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{isAdmin ? "Lending Manager" : "My Borrowed Books"}</DialogTitle></DialogHeader>
          {isAdmin ? <BorrowManager books={books} /> : <ProfileBorrows />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
